/**
 * The GitHub install outcome, on whatever page the flow returned to. Reads
 * the param once, takes it off the URL so a reload does not repeat it, and
 * for a request to the org owners offers Check again: the link-existing
 * flow, which links the installation once GitHub lists it under this person.
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  GITHUB_OUTCOME_PARAMS,
  readGitHubInstallOutcome,
  type GitHubInstallOutcome as Outcome,
} from "../lib/githubInstallOutcome.js";
import { oauthInstallUrl } from "../lib/oauthInstallUrls.js";
import { Card } from "../ui/index.js";

export function GitHubInstallOutcome({ returnPath }: { returnPath: string }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [outcome, setOutcome] = useState<Outcome | null>(() => readGitHubInstallOutcome(location.search));

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!GITHUB_OUTCOME_PARAMS.some((key) => params.has(key))) return;
    const next = readGitHubInstallOutcome(location.search);
    if (next) setOutcome(next);
    for (const key of GITHUB_OUTCOME_PARAMS) params.delete(key);
    const clean = params.toString();
    navigate(
      { pathname: location.pathname, search: clean ? `?${clean}` : "", hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [location.pathname, location.search, location.hash, location.state, navigate]);

  if (!outcome) return null;
  return (
    <Card
      tone="info"
      padding="sm"
      className="text-sm"
      role={outcome.kind === "error" ? "alert" : "status"}
      data-testid="github-install-outcome"
    >
      {outcome.message}
      {outcome.kind === "requested" && (
        <>
          {" "}
          <a
            href={oauthInstallUrl("github", returnPath, { githubMode: "authorize" })}
            className="underline"
            data-testid="github-check-again"
          >
            Check again
          </a>
        </>
      )}
    </Card>
  );
}
